import { queryOptions, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { WorkItemService } from "@/services/work-item.service";
import type { TGanttDependency, TGanttDependencyType } from "@/types/gantt";
import type { TDependencySubtype } from "@/types/issue";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The work-item relations family (NG-2812), `[slug, uid, 'work-item-relations',
 * <scope>, <id>]`. This PR defines the project scope the Gantt draws its
 * dependency lines from, `[…, 'project', projectId]`: every same-project
 * `blocked_by` and `relates_to` edge in one read. The per-item scopes belong to
 * the work-item relations panel (NG-2806), which invalidates the same prefix
 * from its writers, so an edge edited on a work item redraws an open Gantt.
 *
 * Freshness: `mutable`, without the 60 s visible poll; the store never polled
 * it. Focus and a revisit re-read it once stale.
 *
 * @writers `createProjectDependency` and `deleteProjectDependency` (the Gantt's
 * drag-to-link and its delete), each optimistic with its own rollback, then a
 * re-read of the whole prefix.
 */

type RelationsReader = Pick<WorkItemService, "listProjectRelations">;
type RelationsWriter = Pick<WorkItemService, "createRelations" | "removeRelation">;

const POLICY = { ...FRESHNESS.mutable, refetchInterval: false } as const;

/** Every relations read in `scope`, the prefix a relation write re-reads. */
export function workItemRelationsPrefix(scope: QueryScope): QueryKey {
  return scopedKey(scope, "work-item-relations");
}

export function projectRelationsKey(scope: QueryScope, projectId: string) {
  return scopedKey(scope, "work-item-relations", "project", projectId);
}

/** A project's dependency edges, as the Gantt draws them. */
export function projectRelationsQuery(
  service: RelationsReader,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...POLICY,
    queryKey: projectRelationsKey(scope, projectId),
    queryFn: scopedRead(
      (request) => service.listProjectRelations(scope.tenant, projectId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * Whether an edge `source → target` would close a loop over `dependencies`:
 * the same item, or a path already running from `target` back to `source`.
 */
export function wouldCreateCycle(
  dependencies: readonly TGanttDependency[],
  sourceIssueId: string,
  targetIssueId: string,
): boolean {
  if (sourceIssueId === targetIssueId) return true;
  const graph = new Map<string, string[]>();
  for (const d of dependencies) {
    const list = graph.get(d.sourceIssueId) ?? [];
    list.push(d.targetIssueId);
    graph.set(d.sourceIssueId, list);
  }
  const queue: string[] = [targetIssueId];
  const seen = new Set<string>();
  while (queue.length) {
    const node = queue.shift()!;
    if (node === sourceIssueId) return true;
    if (seen.has(node)) continue;
    seen.add(node);
    for (const next of graph.get(node) ?? []) queue.push(next);
  }
  return false;
}

/**
 * Put `dependencies` under `key` while a write is in flight, and hand back what
 * was there for the rollback. A read in flight is cancelled first so its
 * answer cannot overwrite the optimistic edge.
 */
async function applyOptimistic(
  client: QueryClient,
  key: QueryKey,
  next: (current: TGanttDependency[]) => TGanttDependency[],
): Promise<TGanttDependency[] | undefined> {
  await client.cancelQueries({ queryKey: key });
  const prior = client.getQueryData<TGanttDependency[]>(key);
  client.setQueryData<TGanttDependency[]>(key, next(prior ?? []));
  return prior;
}

function rereadRelations(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: workItemRelationsPrefix(scope) });
}

export interface NewProjectDependency {
  sourceIssueId: string;
  targetIssueId: string;
  relationType: TGanttDependencyType;
  subtype?: TDependencySubtype | null;
}

/**
 * Link two of a project's work items. The edge shows at once under a
 * temporary id, then the re-read replaces it with the server's. A refused write
 * puts the project's edges back and rethrows, so the caller can say why.
 */
export async function createProjectDependency(
  client: QueryClient,
  service: RelationsWriter,
  scope: QueryScope,
  projectId: string,
  { sourceIssueId, targetIssueId, relationType, subtype }: NewProjectDependency,
  source: AnalyticsSource = "gantt",
): Promise<void> {
  // Only blocked_by carries a subtype; relates_to has none.
  const resolvedSubtype = relationType === "blocked_by" ? (subtype ?? null) : null;
  const key = projectRelationsKey(scope, projectId);
  const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const prior = await applyOptimistic(client, key, (current) => [
    ...current,
    {
      id: tempId,
      sourceIssueId,
      targetIssueId,
      relation_type: relationType,
      subtype: resolvedSubtype,
    },
  ]);

  // The relation is stored on the blocked item, naming its blocker.
  const anchorIssueId = relationType === "blocked_by" ? targetIssueId : sourceIssueId;
  const relatedIssueId = relationType === "blocked_by" ? sourceIssueId : targetIssueId;
  try {
    await service.createRelations(scope.tenant, projectId, anchorIssueId, {
      relation_type: relationType,
      related_list: [
        { relation_type: relationType, related_issue: relatedIssueId, subtype: resolvedSubtype },
      ],
    });
  } catch (error) {
    client.setQueryData<TGanttDependency[]>(key, prior ?? []);
    throw error;
  }
  // Before the re-read: the dependency exists even if reloading them fails.
  track("dependency_created", {
    project_id: projectId,
    source_task_id: sourceIssueId,
    target_task_id: targetIssueId,
    relation_type: relationType,
    source,
  });
  await rereadRelations(client, scope);
}

/**
 * Remove one of a project's edges by its id. It leaves the Gantt at once; a
 * refused write puts the project's edges back and rethrows. An id the cache
 * does not hold does nothing.
 */
export async function deleteProjectDependency(
  client: QueryClient,
  service: RelationsWriter,
  scope: QueryScope,
  projectId: string,
  relationId: string,
  source: AnalyticsSource = "gantt",
): Promise<void> {
  const key = projectRelationsKey(scope, projectId);
  const dep = client.getQueryData<TGanttDependency[]>(key)?.find((d) => d.id === relationId);
  if (!dep) return;
  const prior = await applyOptimistic(client, key, (current) =>
    current.filter((d) => d.id !== relationId),
  );
  const anchorIssueId = dep.relation_type === "blocked_by" ? dep.targetIssueId : dep.sourceIssueId;
  const relatedIssueId = dep.relation_type === "blocked_by" ? dep.sourceIssueId : dep.targetIssueId;
  try {
    await service.removeRelation(
      scope.tenant,
      projectId,
      anchorIssueId,
      relatedIssueId,
      dep.relation_type,
    );
  } catch (error) {
    client.setQueryData<TGanttDependency[]>(key, prior ?? []);
    throw error;
  }
  track("dependency_deleted", {
    project_id: projectId,
    source_task_id: dep.sourceIssueId,
    target_task_id: dep.targetIssueId,
    relation_type: dep.relation_type,
    source,
  });
  await rereadRelations(client, scope);
}
